import { styled } from '@mui/material/styles';
import { getIdvThemeTokens } from '../themeTokens';
import type { DispositionBadgeTone, MarkAsVariant } from './types';

export const AttemptsRoot = styled('section')({
  margin: '22px 0 0',
});

export const SectionHeading = styled('div')(({ theme }) => ({
  marginBottom: 12,
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  gap: 16,
  [theme.breakpoints.down(760)]: {
    alignItems: 'stretch',
    flexDirection: 'column',
  },
}));

export const AttemptsEyebrow = styled('p')(({ theme }) => ({
  margin: '0 0 4px',
  color: getIdvThemeTokens(theme).textMuted,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
}));

export const AttemptsTitle = styled('h3')(({ theme }) => ({
  margin: 0,
  fontSize: 15,
  fontWeight: 700,
  lineHeight: 1.3,
  color: getIdvThemeTokens(theme).text,
}));

export const EmptyAttemptsNote = styled('p')(({ theme }) => ({
  margin: 0,
  color: getIdvThemeTokens(theme).textMuted,
  fontSize: 13,
  lineHeight: 1.45,
}));

export const AttemptsCount = styled('span')(({ theme }) => ({
  color: getIdvThemeTokens(theme).textMuted,
  fontWeight: 500,
}));

export const AttemptList = styled('div')({
  display: 'grid',
  gap: 10,
});

export const AttemptRow = styled('article', {
  shouldForwardProp: (prop) => prop !== 'resolving',
})<{ resolving: boolean }>(({ theme, resolving }) => {
  const t = getIdvThemeTokens(theme);
  return {
    padding: '14px 16px',
    border: `1px solid ${resolving ? t.resolvingBorder : t.border}`,
    borderRadius: 10,
    background: resolving ? t.resolvingFill : t.surface,
    boxShadow: resolving ? `inset 3px 0 0 ${t.resolvingBar}` : 'none',
  };
});

export const AttemptTop = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  marginBottom: 6,
  [theme.breakpoints.down(640)]: {
    alignItems: 'flex-start',
    flexDirection: 'column',
  },
}));

export const AttemptIdRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 8,
});

export const AttemptId = styled('strong')(({ theme }) => ({
  color: getIdvThemeTokens(theme).text,
  fontSize: 13,
}));

export const AttemptTime = styled('span')(({ theme }) => ({
  color: getIdvThemeTokens(theme).textMuted,
  fontSize: 11,
  fontWeight: 600,
}));

export const AiBadge = styled('span')(({ theme }) => {
  const t = getIdvThemeTokens(theme);
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1px 5px',
    borderRadius: 4,
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: '0.04em',
    lineHeight: 1.4,
    background: t.violetSoft,
    color: t.aiText,
    border: `1px solid ${t.aiBadgeBorder}`,
  };
});

export const DispositionBadge = styled('span', {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone: DispositionBadgeTone }>(({ theme, tone }) => {
  const t = getIdvThemeTokens(theme);
  const variants: Record<DispositionBadgeTone, { color: string; background: string }> = {
    warning: { color: t.amber, background: t.dispWarningBg },
    success: { color: t.green, background: t.dispSolvedBg },
    info: { color: t.banner.info.color, background: t.tagOverrideBg },
  };
  return {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '3px 8px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    ...variants[tone],
  };
});

export const ResolveFlag = styled('span')(({ theme }) => {
  const t = getIdvThemeTokens(theme);
  return {
    padding: '3px 8px',
    borderRadius: 20,
    border: `1px solid ${t.resolvingBorder}`,
    color: t.green,
    background: t.banner.success.background,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
  };
});

export const AttemptSummary = styled('p')(({ theme }) => ({
  margin: '0 0 10px',
  color: getIdvThemeTokens(theme).textMeta,
  fontSize: 12,
}));

export const AttemptAiLine = styled('p')(({ theme }) => {
  const t = getIdvThemeTokens(theme);
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    margin: '-4px 0 10px',
    padding: '6px 8px',
    borderRadius: 8,
    background: t.violetSoft,
    color: t.aiText,
    fontSize: 11,
    fontWeight: 600,
  };
});

export const AiLineTag = styled('span')(({ theme }) => {
  const t = getIdvThemeTokens(theme);
  return {
    padding: '1px 5px',
    borderRadius: 4,
    background: t.surface,
    border: `1px solid ${t.aiBadgeBorder}`,
    fontSize: 9,
    letterSpacing: '0.04em',
    flex: '0 0 auto',
  };
});

const docChipBase = (theme: Parameters<typeof getIdvThemeTokens>[0]) => {
  const t = getIdvThemeTokens(theme);
  return {
    padding: '5px 8px 5px 5px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    border: `1px solid ${t.border}`,
    borderRadius: 6,
    color: t.textMeta,
    background: t.surfaceSoft,
    fontSize: 11,
    textDecoration: 'none',
  };
};

export const DocChips = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  marginBottom: 10,
});

export const DocChipLink = styled('a')(({ theme }) => {
  const t = getIdvThemeTokens(theme);
  return {
    ...docChipBase(theme),
    cursor: 'pointer',
    '&:hover': {
      borderColor: t.chipHoverBorder,
      color: t.violetDark,
      textDecoration: 'none',
    },
  };
});

export const DocChipPlain = styled('span')(({ theme }) => ({
  ...docChipBase(theme),
  cursor: 'default',
}));

export const DocThumb = styled('i')(({ theme }) => {
  const t = getIdvThemeTokens(theme);
  return {
    width: 23,
    height: 23,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    color: t.violetDark,
    background: t.violetSoft,
    fontSize: 8,
    fontStyle: 'normal',
    fontWeight: 700,
  };
});

export const DocId = styled('small')(({ theme }) => ({
  color: getIdvThemeTokens(theme).textMuted,
}));

export const DocExt = styled('span')(({ theme }) => {
  const t = getIdvThemeTokens(theme);
  return {
    marginLeft: 2,
    padding: '1px 5px',
    borderRadius: 4,
    background: t.tagDefaultBg,
    color: t.textMeta,
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: '0.03em',
  };
});

export const AttemptFoot = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  [theme.breakpoints.down(640)]: {
    alignItems: 'stretch',
    flexDirection: 'column',
  },
}));

export const AttemptNote = styled('p')(({ theme }) => ({
  margin: 0,
  color: getIdvThemeTokens(theme).textMuted,
  fontSize: 11,
}));

export const MarkAsButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'markVariant',
})<{ markVariant: MarkAsVariant }>(({ theme, markVariant }) => {
  const t = getIdvThemeTokens(theme);
  const primary = markVariant === 'primary';
  return {
    flex: '0 0 auto',
    minHeight: 34,
    padding: '7px 12px',
    border: `1px solid ${primary ? t.violet : t.borderStrong}`,
    borderRadius: 7,
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
    color: primary ? t.markPrimaryText : t.markSecondaryText,
    background: primary ? t.violet : t.surface,
    '&:disabled': {
      opacity: 0.45,
      cursor: 'not-allowed',
    },
    '&:not(:disabled):hover': primary
      ? {
          background: t.violetDark,
        }
      : undefined,
  };
});
